import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  createConversation,
  deleteProvider,
  updateSelectedMessage,
} from '@store/actions'
import { Provider } from '@dataTypes/provider.types'
import { ChatChannel } from '@dataTypes/chat.type'
import { IContact } from '@dataTypes/contact.type'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { getFullName } from '@utils/fullname.util'
import { SET_ACTIVE_TAB } from '@store/reducers/messageCenter.reducer'
import { closeSidePanel } from '@store/reducers/uiSlice.reducer'

export interface UseProviderActionsReturn {
  // Action handlers
  handleCallClick: (provider: Provider) => void
  handleEmailClick: (provider: Provider) => void
  handleMessageClick: (provider: Provider) => void
  handleEditProvider: (provider: Provider) => void
  handleDeleteProvider: (provider: Provider) => void
  confirmDeleteProvider: (provider: Provider) => void
  handleAddProvider: () => void

  // Modal state
  showCallModal: boolean
  showEmailSender: boolean
  showProviderModal: boolean
  showConfirmDeleteModal: boolean
  selectedProvider: Provider | null
  selectedProviderContact: Provider | null
  providerToDelete: Provider | null

  // Modal close handlers
  handleCloseCallModal: () => void
  handleCloseEmailSender: () => void
  handleCloseProviderModal: () => void
  handleCloseConfirmDeleteModal: () => void

  // Modal state for rendering modals in components
  modalState: {
    showCallModal: boolean
    showEmailSender: boolean
    showProviderModal: boolean
    showConfirmDeleteModal: boolean
    selectedProvider: Provider | null
    selectedProviderContact: Provider | null
    providerToDelete: Provider | null
  }
}

export const useProviderActions = (callbacks?: {
  onAfterDeleteSuccess?: () => void
}): UseProviderActionsReturn => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { showToast } = useNotificationToast()

  // Redux state
  const conversations = useAppSelector((state) => state.chat.data)
  const contacts = useAppSelector((state) => state.contacts.data ?? [])

  // Local state
  const [showCallModal, setShowCallModal] = useState(false)
  const [showEmailSender, setShowEmailSender] = useState(false)
  const [showProviderModal, setShowProviderModal] = useState(false)
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null,
  )
  const [selectedProviderContact, setSelectedProviderContact] =
    useState<Provider | null>(null)
  const [providerToDelete, setProviderToDelete] = useState<Provider | null>(
    null,
  )

  // Action handlers
  const handleCallClick = useCallback((provider: Provider): void => {
    setSelectedProviderContact(provider)
    setShowCallModal(true)
  }, [])

  const handleEmailClick = useCallback((provider: Provider): void => {
    setSelectedProviderContact(provider)
    setShowEmailSender(true)
  }, [])

  const handleSendMessage = useCallback(
    (contact: IContact): void => {
      dispatch(SET_ACTIVE_TAB('chat'))
      const chat = conversations?.find((c) => {
        const participant = c.chat_participant
        return participant && participant.length == 1 && participant[0].contact
          ? participant[0].contact.contact_id === contact.contact_id
          : false
      })

      if (chat && chat.chat_conversation_id) {
        dispatch(updateSelectedMessage({ id: chat.chat_conversation_id }))
      } else {
        dispatch(
          createConversation({
            conversation_data: {
              channel: ChatChannel.sms,
              chat_message: [],
              chat_participant: [],
            },
            members_data: [
              {
                chat_conversation_id: '',
                phone_number: contact.cellphone,
                contact_id: contact.contact_id ?? undefined,
              },
            ],
          }),
        )
      }
    },
    [dispatch, conversations],
  )

  const handleMessageClick = useCallback(
    (provider: Provider): void => {
      const providerContact = contacts.find(
        (c) => c.provider_id === provider?.provider_id,
      )
      if (providerContact) {
        handleSendMessage(providerContact)
        navigate('/message')
      }
    },
    [contacts, handleSendMessage, navigate],
  )

  // Check if provider is mock data (production-safe guard)
  const isMockProvider = useCallback((provider: Provider): boolean => {
    return provider.provider_id?.startsWith('mock-') ?? false
  }, [])

  const handleEditProvider = useCallback(
    (provider: Provider): void => {
      if (isMockProvider(provider)) {
        showToast(
          createFailedToast(
            'Cannot edit staff',
            'Cannot edit mock staff records.',
          ),
        )
        return
      }
      setSelectedProvider(provider)
      setShowProviderModal(true)
    },
    [isMockProvider, showToast],
  )

  const handleDeleteProvider = useCallback(
    (provider: Provider): void => {
      if (isMockProvider(provider)) {
        const message = 'Cannot delete mock staff records'
        showToast(createFailedToast('Delete staff failed', message))
        return
      }
      setProviderToDelete(provider)
      setShowConfirmDeleteModal(true)
    },
    [isMockProvider, showToast],
  )

  const handleAddProvider = useCallback((): void => {
    setSelectedProvider(null)
    setShowProviderModal(true)
  }, [])

  const confirmDeleteProvider = useCallback(
    (provider: Provider): void => {
      if (isMockProvider(provider)) {
        const message = 'Cannot delete mock staff records'
        showToast(createFailedToast('Delete staff failed', message))
        setShowConfirmDeleteModal(false)
        return
      }

      const providerId = provider.provider_id ?? ''
      const providerName = getFullName(provider) ?? 'Staff'

      dispatch(deleteProvider(providerId))
        .unwrap()
        .then(() => {
          dispatch(closeSidePanel())
          showToast(
            createSuccessToast(
              'Staff deleted successfully',
              `"${providerName}" has been deleted successfully.`,
            ),
          )
          callbacks?.onAfterDeleteSuccess?.()
        })
        .catch((error) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to delete staff. Please try again.'
          showToast(createFailedToast('Failed to delete staff', message))
        })
      setShowConfirmDeleteModal(false)
    },
    [dispatch, isMockProvider, showToast, callbacks],
  )

  // Modal close handlers
  const handleCloseCallModal = useCallback((): void => {
    setShowCallModal(false)
    setSelectedProviderContact(null)
  }, [])

  const handleCloseEmailSender = useCallback((): void => {
    setShowEmailSender(false)
    setSelectedProviderContact(null)
  }, [])

  const handleCloseProviderModal = useCallback((): void => {
    setShowProviderModal(false)
    setSelectedProvider(null)
  }, [])

  const handleCloseConfirmDeleteModal = useCallback((): void => {
    setShowConfirmDeleteModal(false)
    setProviderToDelete(null)
  }, [])

  // Modal state object
  const modalState = {
    showCallModal,
    showEmailSender,
    showProviderModal,
    showConfirmDeleteModal,
    selectedProvider,
    selectedProviderContact,
    providerToDelete,
  }

  return {
    // Action handlers
    handleCallClick,
    handleEmailClick,
    handleMessageClick,
    handleEditProvider,
    handleDeleteProvider,
    confirmDeleteProvider,
    handleAddProvider,

    // Modal state
    showCallModal,
    showEmailSender,
    showProviderModal,
    showConfirmDeleteModal,
    selectedProvider,
    selectedProviderContact,
    providerToDelete,

    // Modal close handlers
    handleCloseCallModal,
    handleCloseEmailSender,
    handleCloseProviderModal,
    handleCloseConfirmDeleteModal,

    // Modal state for rendering modals in components
    modalState,
  }
}
